import { motion } from 'framer-motion';
import { ArrowRight, Shield, BarChart3, Zap, Users, CheckCircle2, Mail, Sliders, TrendingUp, Lock } from 'lucide-react';

const InsuranceClaims = () => {
  const features = [
    {
      icon: <Shield className="w-12 h-12 text-accent-500" />,
      title: "White-labelled Intelligent Tools Deployed as a Service",
      description: "Deliver digital transformation benefits safely, under your brand, without the complexity of full tech projects",
    },
  ];

  const capabilities = [
    {
      icon: <Zap className="w-8 h-8 text-primary-600" />,
      title: "Customer Triage Based on Eligibility & Affordability",
      description: "Increase engagement with SMEs while keeping operational costs low and improving conversion rates",
    },
    {
      icon: <Users className="w-8 h-8 text-primary-600" />,
      title: "Single Point of Access to Connect Funders & Brokers",
      description: "Generate high-quality, compliant proposals with secure submission and real-time validation of funder appetite",
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-primary-600" />,
      title: "In-life Portfolio Monitoring",
      description: "Monitor affordability trends and risk across the portfolio, combining heat mapping with individual customer deep dives",
    },
  ];

  const trustFeatures = [
    {
      icon: <Sliders className="w-12 h-12 text-primary-600" />,
      title: "You're in Control",
      description: "Choose the right finance for your business based on personalised quotes from more than 70 lenders.",
    },
    {
      icon: <Zap className="w-12 h-12 text-primary-600" />,
      title: "Simple & Fast",
      description: "Complete one funding request in 3 minutes to access quotes, receive money in your account in 24 hours.",
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-primary-600" />,
      title: "Fair & Transparent",
      description: "Easily compare the terms available to your business – no jargon, and never more expensive than direct.",
    },
    {
      icon: <Lock className="w-12 h-12 text-primary-600" />,
      title: "Safe & Secure",
      description: "Apply with confidence, no impact on your credit score, we're regulated by the FCA, accredited by HM Treasury.",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                Lending.{' '}
                <span className="gradient-text">Digitised.</span>
              </h1>
              <p className="text-xl text-gray-600 font-medium">
                Our suite of SME lending solutions transforms digital customer experiences, reduces operating costs, increases look-to-book ratios, and delivers insight into in-life portfolio trends.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="insuranceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#insuranceGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <Shield className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="overviewInsuranceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#overviewInsuranceGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="150" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="150" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="200" width="200" height="80" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="230" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Transforming customer engagement and delivering insight into SME lending
              </h2>
              <p className="text-gray-600 leading-relaxed">
                At 1ST TRUST FINANCES, we provide intelligent lending tools that deliver the benefits of digital transformation without the risk of complex and costly technology projects.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* White-labelled Intelligent Tools */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                  <div className="flex-shrink-0">{feature.icon}</div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility & Affordability Assessment */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Eligibility and affordability assessment configured to your own risk models
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Streamline operations by screening deals or portfolios and update risk models in real-time using 200+ eligibility criteria.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="eligibilityGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#eligibilityGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <Shield className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Real-time Insights */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="insightsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#insightsGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <BarChart3 className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Real-time insights fuelled by validated transactional data
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Deliver real-time insights from Experian CAIS, Equifax Insights, and CCDS data to enable eligibility and affordability assessments.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Digital Lending Capabilities */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Digital Lending Capabilities
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive tools to transform your lending operations.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{capability.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {capability.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients & Reach */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Trusted by Leading Lenders & Partners
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              1ST TRUST FINANCES works with more than 80 banks, lenders, and funders. Our technology and analytics solutions are transforming customer conversion and engagement, setting the standard in SME lending portfolio monitoring.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Contact Form */}
      <section className="section gradient-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 1000" className="w-full h-full">
            <defs>
              <pattern id="grid6" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid6)" />
          </svg>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              See How 1ST TRUST FINANCES Can Help You Today
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Connect with our team to learn more about our SME lending solutions.
            </p>
            <form className="bg-white rounded-xl p-8 shadow-2xl">
              <div className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-left text-gray-700 font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full"
                >
                  FIND OUT MORE
                  <ArrowRight className="inline ml-2 w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                By clicking the button, you acknowledge our privacy policy and understand that a member of the FIRST TRUST FINANCES team will contact you via the email provided.
              </p>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InsuranceClaims;
